'use client';

import _defineProperty from "/codebuild/output/src3136/src/s3/01/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/codebuild/output/src3136/src/s3/01/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["classNames", "className", "style", "styles", "unstyled", "vars", "size", "variant", "__staticSelector", "inputContainer", "inputWrapperOrder", "label", "error", "description", "labelProps", "descriptionProps", "errorProps", "labelElement", "children", "withAsterisk", "id", "required", "__stylesApiProps", "mod"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { jsx } from 'react/jsx-runtime';
import { Fragment, createElement } from 'react';
import { useId } from '@mantine/hooks';
import { rem } from '../../../core/utils/units-converters/rem.mjs';
import { getFontSize } from '../../../core/utils/get-size/get-size.mjs';
import { createVarsResolver } from '../../../core/styles-api/create-vars-resolver/create-vars-resolver.mjs';
import 'clsx';
import '../../../core/MantineProvider/Mantine.context.mjs';
import '../../../core/MantineProvider/default-theme.mjs';
import '../../../core/MantineProvider/MantineProvider.mjs';
import '../../../core/MantineProvider/MantineThemeProvider/MantineThemeProvider.mjs';
import { useProps } from '../../../core/MantineProvider/use-props/use-props.mjs';
import '../../../core/MantineProvider/MantineCssVariables/MantineCssVariables.mjs';
import { useStyles } from '../../../core/styles-api/use-styles/use-styles.mjs';
import { Box } from '../../../core/Box/Box.mjs';
import { factory } from '../../../core/factory/factory.mjs';
import '../../../core/DirectionProvider/DirectionProvider.mjs';
import { InputDescription } from '../InputDescription/InputDescription.mjs';
import { InputError } from '../InputError/InputError.mjs';
import { InputLabel } from '../InputLabel/InputLabel.mjs';
import { InputWrapperProvider } from '../InputWrapper.context.mjs';
import { getInputOffsets } from './get-input-offsets/get-input-offsets.mjs';
import classes from '../Input.module.css.mjs';
var defaultProps = {
  labelElement: "label",
  inputContainer: function inputContainer(children) {
    return children;
  },
  inputWrapperOrder: ["label", "description", "input", "error"]
};
var varsResolver = createVarsResolver(function (_, _ref) {
  var size = _ref.size;
  return {
    label: {
      "--input-label-size": getFontSize(size),
      "--input-asterisk-color": void 0
    },
    error: {
      "--input-error-size": size === void 0 ? void 0 : "calc(".concat(getFontSize(size), " - ").concat(rem(2), ")")
    },
    description: {
      "--input-description-size": size === void 0 ? void 0 : "calc(".concat(getFontSize(size), " - ").concat(rem(2), ")")
    }
  };
});
var InputWrapper = factory(function (_props, ref) {
  var props = useProps("InputWrapper", defaultProps, _props);
  var classNames = props.classNames,
    className = props.className,
    style = props.style,
    styles = props.styles,
    unstyled = props.unstyled,
    vars = props.vars,
    size = props.size,
    variant = props.variant,
    __staticSelector = props.__staticSelector,
    inputContainer = props.inputContainer,
    inputWrapperOrder = props.inputWrapperOrder,
    label = props.label,
    error = props.error,
    description = props.description,
    labelProps = props.labelProps,
    descriptionProps = props.descriptionProps,
    errorProps = props.errorProps,
    labelElement = props.labelElement,
    children = props.children,
    withAsterisk = props.withAsterisk,
    id = props.id,
    required = props.required,
    __stylesApiProps = props.__stylesApiProps,
    mod = props.mod,
    others = _objectWithoutProperties(props, _excluded);
  var getStyles = useStyles({
    name: ["InputWrapper", __staticSelector],
    props: __stylesApiProps || props,
    classes: classes,
    className: className,
    style: style,
    classNames: classNames,
    styles: styles,
    unstyled: unstyled,
    vars: vars,
    varsResolver: varsResolver
  });
  var sharedProps = {
    size: size,
    variant: variant,
    __staticSelector: __staticSelector
  };
  var idBase = useId(id);
  var isRequired = typeof withAsterisk === "boolean" ? withAsterisk : required;
  var errorId = (errorProps === null || errorProps === void 0 ? void 0 : errorProps.id) || "".concat(idBase, "-error");
  var descriptionId = (descriptionProps === null || descriptionProps === void 0 ? void 0 : descriptionProps.id) || "".concat(idBase, "-description");
  var inputId = idBase;
  var hasError = !!error && typeof error !== "boolean";
  var hasDescription = !!description;
  var _describedBy = "".concat(hasError ? errorId : "", " ").concat(hasDescription ? descriptionId : "");
  var describedBy = _describedBy.trim().length > 0 ? _describedBy.trim() : void 0;
  var labelId = (labelProps === null || labelProps === void 0 ? void 0 : labelProps.id) || "".concat(idBase, "-label");
  var _label = label && /* @__PURE__ */jsx(InputLabel, _objectSpread(_objectSpread(_objectSpread({
    labelElement: labelElement,
    id: labelId,
    htmlFor: inputId,
    required: isRequired
  }, sharedProps), labelProps), {}, {
    children: label
  }), "label");
  var _description = hasDescription && /* @__PURE__ */jsx(InputDescription, _objectSpread(_objectSpread(_objectSpread({}, descriptionProps), sharedProps), {}, {
    size: (descriptionProps === null || descriptionProps === void 0 ? void 0 : descriptionProps.size) || sharedProps.size,
    id: (descriptionProps === null || descriptionProps === void 0 ? void 0 : descriptionProps.id) || descriptionId,
    children: description
  }), "description");
  var _input = /* @__PURE__ */jsx(Fragment, {
    children: inputContainer(children)
  }, "input");
  var _error = hasError && /* @__PURE__ */createElement(InputError, _objectSpread(_objectSpread(_objectSpread({}, errorProps), sharedProps), {}, {
    size: (errorProps === null || errorProps === void 0 ? void 0 : errorProps.size) || sharedProps.size,
    key: "error",
    id: (errorProps === null || errorProps === void 0 ? void 0 : errorProps.id) || errorId
  }), error);
  var content = inputWrapperOrder.map(function (part) {
    switch (part) {
      case "label":
        return _label;
      case "input":
        return _input;
      case "description":
        return _description;
      case "error":
        return _error;
      default:
        return null;
    }
  });
  return /* @__PURE__ */jsx(InputWrapperProvider, {
    value: _objectSpread({
      getStyles: getStyles,
      describedBy: describedBy,
      inputId: inputId,
      labelId: labelId
    }, getInputOffsets(inputWrapperOrder, {
      hasDescription: hasDescription,
      hasError: hasError
    })),
    children: /* @__PURE__ */jsx(Box, _objectSpread(_objectSpread(_objectSpread({
      ref: ref,
      variant: variant,
      size: size,
      mod: [{
        error: !!error
      }, mod]
    }, getStyles("root")), others), {}, {
      children: content
    }))
  });
});
InputWrapper.classes = classes;
InputWrapper.displayName = "@mantine/core/InputWrapper";
export { InputWrapper };